import {
  FACE_BOUNDS,
  K_SYMMETRY,
  LIGHTING_OK,
  LIGHTING_PENALTY_CAP,
  MIDLINE_INDICES,
  PHI_INVERSE,
  PHI_TOLERANCE,
  SYMMETRY_PAIRS,
  THIRDS_INDICES,
  WEIGHTS,
} from './constants.ts';

export interface Landmark {
  x: number;
  y: number;
  z?: number;
  visibility?: number;
}

export interface FrameScore {
  raw: number;
  sym: number;
  thirds: number;
  phi: number;
  visibility: number;
}

/**
 * Compute a 0-10 score from a single frame of FaceLandmarker output.
 * Returns `null` if landmarks are missing or invalid.
 */
export function scoreFrame(landmarks: Landmark[]): FrameScore | null {
  if (!landmarks || landmarks.length < 478) return null;

  const sym = symmetryScore(landmarks);
  const thirds = thirdsScore(landmarks);
  const phi = phiScore(landmarks);
  const visibility = avgVisibility(landmarks);
  if (sym == null || thirds == null || phi == null) return null;

  const raw =
    10 * (WEIGHTS.sym * sym + WEIGHTS.thirds * thirds + WEIGHTS.phi * phi);
  return { raw: clamp(raw, 0, 10), sym, thirds, phi, visibility };
}

/**
 * Aggregate per-frame scores into a final match score (0-10) plus the
 * lighting penalty. `nullish` samples are dropped from the average.
 */
export function aggregate(frames: (FrameScore | null)[]): {
  mean: number;
  visibility: number;
  validFrames: number;
} {
  const valid = frames.filter((f): f is FrameScore => f !== null);
  if (valid.length === 0) return { mean: 0, visibility: 0, validFrames: 0 };
  const meanRaw = avg(valid.map((f) => f.raw));
  const meanVis = avg(valid.map((f) => f.visibility));
  const final =
    meanVis < LIGHTING_OK
      ? meanRaw - Math.min(LIGHTING_PENALTY_CAP, (2.0 * (LIGHTING_OK - meanVis)) / LIGHTING_OK)
      : meanRaw;
  // Soften the curve: the strict component metrics tend to land in 2–5 for
  // realistic faces; users intuitively expect "average" to be 6–7.
  // Power 0.55 lifts low-mid scores while preserving ordering.
  const softened = 10 * Math.pow(Math.max(0, final) / 10, 0.55);
  return { mean: clamp(softened, 0, 10), visibility: meanVis, validFrames: valid.length };
}

function symmetryScore(lm: Landmark[]): number | null {
  const upper = lm[MIDLINE_INDICES.upper];
  const lower = lm[MIDLINE_INDICES.lower];
  if (!upper || !lower) return null;
  const midX = (upper.x + lower.x) / 2;

  // Bounding-box width to normalise the slack.
  const leftCheek = lm[FACE_BOUNDS.leftCheek];
  const rightCheek = lm[FACE_BOUNDS.rightCheek];
  if (!leftCheek || !rightCheek) return null;
  const bboxW = Math.abs(rightCheek.x - leftCheek.x) || 1;

  let totalDelta = 0;
  let count = 0;
  for (const [li, ri] of SYMMETRY_PAIRS) {
    const l = lm[li];
    const r = lm[ri];
    if (!l || !r) continue;
    const expectedRx = midX + (midX - l.x);
    const dx = Math.abs(expectedRx - r.x);
    const dy = Math.abs(l.y - r.y);
    totalDelta += dx + dy;
    count++;
  }
  if (count === 0) return null;
  const meanDelta = totalDelta / count;
  return clamp(1 - meanDelta / (K_SYMMETRY * bboxW), 0, 1);
}

function thirdsScore(lm: Landmark[]): number | null {
  const hairline = lm[THIRDS_INDICES.hairline];
  const brow = midpoint(lm[THIRDS_INDICES.browLeft], lm[THIRDS_INDICES.browRight]);
  const noseBase = lm[THIRDS_INDICES.noseBase];
  const chin = lm[THIRDS_INDICES.chin];
  if (!hairline || !brow || !noseBase || !chin) return null;

  const a = brow.y - hairline.y;
  const b = noseBase.y - brow.y;
  const c = chin.y - noseBase.y;
  if (a <= 0 || b <= 0 || c <= 0) return 0;
  const m = (a + b + c) / 3;
  const meanAbs = (Math.abs(a - m) + Math.abs(b - m) + Math.abs(c - m)) / 3;
  return clamp(1 - meanAbs / m, 0, 1);
}

function phiScore(lm: Landmark[]): number | null {
  const left = lm[FACE_BOUNDS.leftCheek];
  const right = lm[FACE_BOUNDS.rightCheek];
  const top = lm[FACE_BOUNDS.top];
  const bottom = lm[FACE_BOUNDS.bottom];
  if (!left || !right || !top || !bottom) return null;
  const w = Math.abs(right.x - left.x);
  const h = Math.abs(bottom.y - top.y);
  if (h === 0) return null;
  const ratio = w / h;
  return clamp(1 - Math.min(1, Math.abs(ratio - PHI_INVERSE) / PHI_TOLERANCE), 0, 1);
}

function avgVisibility(lm: Landmark[]): number {
  // MediaPipe face_landmarker leaves `visibility` as a 0 placeholder on every
  // point — only pose_landmarker populates it. Treat the all-zero case as
  // "field not populated by this model" rather than "occluded face."
  let total = 0;
  let count = 0;
  for (let i = 0; i < lm.length; i++) {
    const v = lm[i]?.visibility;
    if (typeof v === 'number' && v > 0) {
      total += v;
      count++;
    }
  }
  return count === 0 ? 1 : total / count;
}

function midpoint(a: Landmark | undefined, b: Landmark | undefined): Landmark | undefined {
  if (!a || !b) return undefined;
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

function avg(xs: number[]): number {
  return xs.reduce((s, v) => s + v, 0) / xs.length;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
